import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ExternalLink, FileDown, RefreshCw, Upload, Search, Filter, Columns3,
  Download, ChevronDown, Trash2, X
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import AdminLayout from "@/components/admin/AdminLayout";
import { adminHttp } from "@/lib/adminApi";

const TABS = ["Overview", "Consumer MI", "NSC", "DTR MI", "Sign Off"];

const IMPORT_OPTIONS = [
  { value: "installation", label: "Installation Import" },
  { value: "inspection", label: "Inspection Import" },
  { value: "signoff", label: "Signoff Import" },
  { value: "template", label: "Create / Import Template" },
];

const SIGNOFF_TYPES = [
  {
    value: "old_meter",
    label: "Old Meter Sign Off",
  },
  {
    value: "cable_70",
    label: "70 SQMM DT Cable Sign Off",
  },
  {
    value: "cable_120",
    label: "120 SQMM DT Cable Sign Off",
  },
  {
    value: "single_phase_cable",
    label: "Single Phase Cable Sign Off",
  },
  {
    value: "three_phase_cable",
    label: "3 Phase Cable Sign Off",
    disabled: true,
  },
];

function formatDate(value) {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleDateString("en-IN");
  } catch {
    return String(value);
  }
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
}

function getCategoryFromTab(tab) {
  if (tab === "Consumer MI") return "consumer";
  if (tab === "NSC") return "nsc";
  if (tab === "DTR MI") return "dtr";
  return "";
}

function getRows(payload) {
  if (!payload) return [];

  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.installations)) return payload.installations;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.rows)) return payload.rows;

  return [];
}

function getCount(payload) {
  if (!payload) return 0;

  if (typeof payload === "number") return payload;

  return (
    payload.total ??
    payload.count ??
    payload.total_count ??
    payload.items_count ??
    getRows(payload).length ??
    0
  );
}

function getInstallationId(row) {
  return row?.id || row?._id || row?.installation_id || row?.ID;
}

function getConsumerName(row) {
  return (
    row?.consumer_name ||
    row?.consumerName ||
    row?.name ||
    row?.data?.["Consumer Name"] ||
    row?.data?.consumer_name ||
    "-"
  );
}

function getAccountNumber(row) {
  return (
    row?.account_number ||
    row?.account_no ||
    row?.consumer_no ||
    row?.consumer_number ||
    row?.data?.["Account Number"] ||
    row?.data?.["Consumer Number"] ||
    "-"
  );
}

function getMeterNumber(row) {
  return (
    row?.meter_number ||
    row?.meter_no ||
    row?.new_meter_serial ||
    row?.new_meter_no ||
    row?.data?.["New Meter No"] ||
    row?.data?.["Meter No"] ||
    "-"
  );
}

function getStatus(row) {
  return (
    row?.status ||
    row?.installation_status ||
    row?.payment_status ||
    row?.data?.status ||
    "Pending"
  );
}

function StatusBadge({ value }) {
  const status = String(value || "Pending");

  const normalized = status.toLowerCase();

  let classes =
    "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-muted text-muted-foreground";

  if (
    normalized.includes("complete") ||
    normalized.includes("approved") ||
    normalized.includes("paid") ||
    normalized.includes("done") ||
    normalized.includes("success")
  ) {
    classes =
      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-green-100 text-green-700";
  } else if (
    normalized.includes("reject") ||
    normalized.includes("cancel") ||
    normalized.includes("fail")
  ) {
    classes =
      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-red-100 text-red-700";
  } else if (
    normalized.includes("inspection") ||
    normalized.includes("progress") ||
    normalized.includes("process")
  ) {
    classes =
      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-700";
  } else if (
    normalized.includes("pending") ||
    normalized.includes("required")
  ) {
    classes =
      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-yellow-100 text-yellow-700";
  }

  return <span className={classes}>{status}</span>;
}

function InstallationOperationsPage() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("Overview");

  const [dashboard, setDashboard] = useState(null);
  const [installations, setInstallations] = useState([]);

  const [signoffBatches, setSignoffBatches] = useState([]);
  const [signoffData, setSignoffData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [error, setError] = useState("");

  const [importMenu, setImportMenu] = useState(false);
  const [exportMenu, setExportMenu] = useState(false);
  const [searchByMenu, setSearchByMenu] = useState(false);
  const [columnMenu, setColumnMenu] = useState(false);
  const [searchField, setSearchField] = useState("Account ID");
  const [searchText, setSearchText] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: "", toDate: "", division: "", subdivision: "",
    substation: "", feeder: "", technician: ""
  });
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  const [selectedInstallation, setSelectedInstallation] = useState(null);

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [inspectionLoading, setInspectionLoading] = useState(false);

  const [selectedSignoffType, setSelectedSignoffType] =
    useState("old_meter");

  const [signoffLoading, setSignoffLoading] = useState(false);

  const [signoffTemplates, setSignoffTemplates] = useState([]);

  const apiError = (err, fallback = "Unable to complete request.") => {
    return (
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.message ||
      fallback
    );
  };

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);

    try {
      const response = await adminHttp.get("/api/operations/dashboard");
      setDashboard(response?.data || null);
      setError("");
    } catch (err) {
      console.error("Operations dashboard error:", err);
      setError(apiError(err, "Unable to load Operations dashboard."));
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  const loadInstallations = useCallback(async () => {
    setLoading(true);

    try {
      const response = await adminHttp.get(
        "/api/operations/installations?limit=100000"
      );

      setInstallations(getRows(response?.data));
      setError("");
    } catch (err) {
      console.error("Operations installations error:", err);
      setError(apiError(err, "Unable to load installation data."));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSignoffBatches = useCallback(async () => {
    try {
      const response = await adminHttp.get(
        "/api/operations/signoff/batches"
      );

      setSignoffBatches(getRows(response?.data));
    } catch (err) {
      console.error("Signoff batches error:", err);
    }
  }, []);

  const loadSignoffData = useCallback(async () => {
    try {
      const response = await adminHttp.get(
        `/api/operations/signoff/data/${selectedSignoffType}`
      );

      setSignoffData(getRows(response?.data));
    } catch (err) {
      console.error("Signoff data error:", err);
      setSignoffData([]);
    }
  }, [selectedSignoffType]);

  const loadSignoffTemplates = useCallback(async () => {
    try {
      const response = await adminHttp.get(
        "/api/operations/signoff/templates"
      );

      setSignoffTemplates(getRows(response?.data));
    } catch (err) {
      console.error("Signoff templates error:", err);
      setSignoffTemplates([]);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    loadInstallations();
  }, [loadInstallations]);

  useEffect(() => {
    if (activeTab === "Sign Off") {
      loadSignoffBatches();
      loadSignoffData();
      loadSignoffTemplates();
    }
  }, [
    activeTab,
    loadSignoffBatches,
    loadSignoffData,
    loadSignoffTemplates,
  ]);

  const refreshAll = async () => {
    setError("");

    await Promise.all([
      loadDashboard(),
      loadInstallations(),
      activeTab === "Sign Off"
        ? loadSignoffBatches()
        : Promise.resolve(),
    ]);

    if (activeTab === "Sign Off") {
      await loadSignoffData();
    }
  };

  const handleImport = async (type) => {
    setImportMenu(false);

    if (type === "template") {
      await loadSignoffTemplates();

      if (signoffTemplates.length > 0) {
        setActiveTab("Sign Off");
      } else {
        alert(
          "Template management is available from the Sign Off section."
        );
      }

      return;
    }

    const input = document.createElement("input");

    input.type = "file";
    input.accept = ".xlsx,.xls,.csv";

    input.onchange = async (event) => {
      const file = event.target.files?.[0];

      if (!file) return;

      setImporting(true);
      setError("");

      try {
        const formData = new FormData();
        formData.append("file", file);

        let url = "/api/operations/import/installation";

        if (type === "inspection") {
          url = "/api/operations/import/inspection";
        }

        if (type === "signoff") {
          url = `/api/operations/import/signoff?signoff_type=${encodeURIComponent(
            selectedSignoffType
          )}`;
        }

        if (type === "installation") {
          const category =
            activeTab === "NSC"
              ? "nsc"
              : activeTab === "DTR MI"
              ? "dtr"
              : "consumer";

          url = `/api/operations/import/installation?category=${category}`;
        }

        const importResponse = await adminHttp.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        await refreshAll();

        if (type === "signoff") {
          await loadSignoffData();
        }

        const imported = importResponse?.data?.imported;
        const duplicates = importResponse?.data?.duplicates || 0;
        if (duplicates > 0) {
          alert(`Import completed. Imported: ${imported ?? 0}. Duplicate rows skipped: ${duplicates}.`);
        } else {
          alert(`Data imported successfully. Imported: ${imported ?? 0}.`);
        }
      } catch (err) {
        console.error("Import error:", err);

        setError(apiError(err, "Unable to import data."));
        alert(apiError(err, "Unable to import data."));
      } finally {
        setImporting(false);
      }
    };

    input.click();
  };

  const openInspection = (row) => {
    const installationId = getInstallationId(row);

    if (!installationId) {
      alert("Installation ID is not available for this record.");
      return;
    }

    navigate("/dt-inspection?tab=new", {
      state: {
        from: "installation-operations",
        installation: row,
        installationId,
      },
    });
  };

  const saveInspectionStatus = async (row, required) => {
    const installationId = getInstallationId(row);

    if (!installationId) {
      alert("Installation ID is not available.");
      return;
    }

    setInspectionLoading(true);

    try {
      await adminHttp.post(
        `/api/operations/installations/${installationId}/inspection`,
        {
          required,
          status: required ? "Required" : "Not Required",
        }
      );

      await loadInstallations();
      await loadDashboard();

      setSelectedInstallation(null);

      alert(
        required
          ? "Site Inspection marked as required."
          : "Site Inspection marked as not required."
      );
    } catch (err) {
      console.error("Inspection status error:", err);
      alert(apiError(err, "Unable to update inspection status."));
    } finally {
      setInspectionLoading(false);
    }
  };

  const markPaymentReady = async (row) => {
    const installationId = getInstallationId(row);

    if (!installationId) {
      alert("Installation ID is not available.");
      return;
    }

    setPaymentLoading(true);

    try {
      await adminHttp.post(
        `/api/operations/installations/${installationId}/payment`,
        {
          status: "Ready",
        }
      );

      await loadInstallations();
      await loadDashboard();

      alert("Payment marked as Ready.");
    } catch (err) {
      console.error("Payment status error:", err);
      alert(apiError(err, "Unable to update payment status."));
    } finally {
      setPaymentLoading(false);
    }
  };

  const generateSignoff = async (format) => {
    setSignoffLoading(true);

    try {
      const response = await adminHttp.post(
        `/api/operations/signoff/generate/${selectedSignoffType}`,
        {
          format,
        }
      );

      await loadSignoffBatches();

      alert(
        response?.data?.message ||
          `${format.toUpperCase()} sign-off generated successfully.`
      );
    } catch (err) {
      console.error("Signoff generation error:", err);

      alert(
        apiError(
          err,
          "Unable to generate sign-off. Please check the selected data."
        )
      );
    } finally {
      setSignoffLoading(false);
    }
  };

  const generatePdf = async () => {
    setSignoffLoading(true);

    try {
      const response = await adminHttp.post(
        `/api/operations/signoff/generate-pdf/${selectedSignoffType}`,
        {},
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${selectedSignoffType}-signoff.pdf`;

      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF generation error:", err);
      alert(apiError(err, "Unable to generate PDF."));
    } finally {
      setSignoffLoading(false);
    }
  };

  const currentRows = useMemo(() => {
    const category = getCategoryFromTab(activeTab);

    if (!category) return installations;

    return installations.filter((row) => {
      const rowCategory =
        row?.category ||
        row?.installation_type ||
        row?.type ||
        row?.data?.category ||
        row?.data?.installation_type;

      if (!rowCategory) return true;

      return String(rowCategory).toLowerCase() === category;
    });
  }, [activeTab, installations]);

  const dashboardCounts = useMemo(() => {
    const source = dashboard || {};

    return {
      consumer:
        source.consumer_mi ??
        source.consumer ??
        source.consumer_count ??
        installations.filter(
          (r) =>
            String(
              r?.category ||
                r?.installation_type ||
                r?.type ||
                r?.data?.category ||
                ""
            ).toLowerCase() === "consumer"
        ).length,

      nsc:
        source.nsc ??
        source.nsc_count ??
        installations.filter(
          (r) =>
            String(
              r?.category ||
                r?.installation_type ||
                r?.type ||
                r?.data?.category ||
                ""
            ).toLowerCase() === "nsc"
        ).length,

      dtr:
        source.dtr_mi ??
        source.dtr ??
        source.dtr_count ??
        installations.filter(
          (r) =>
            String(
              r?.category ||
                r?.installation_type ||
                r?.type ||
                r?.data?.category ||
                ""
            ).toLowerCase() === "dtr"
        ).length,

      signoff:
        source.signoff ??
        source.sign_off ??
        source.signoff_count ??
        signoffBatches.length,
    };
  }, [dashboard, installations, signoffBatches]);

  const renderImportButton = () => (
    <div className="relative">
      <button
        type="button"
        disabled={importing}
        onClick={() => setImportMenu((value) => !value)}
        className="
          inline-flex items-center gap-2
          rounded-lg border border-border
          bg-background px-4 py-2
          text-sm font-semibold
          hover:bg-muted
          disabled:opacity-50
        "
      >
        {importing ? (
          <RefreshCw className="h-4 w-4 animate-spin" />
        ) : (
          <Upload className="h-4 w-4" />
        )}

        Import Data
      </button>

      {importMenu && (
        <div
          className="
            absolute right-0 top-full z-40 mt-2
            w-64 overflow-hidden rounded-xl
            border border-border bg-background
            shadow-xl
          "
        >
          {IMPORT_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleImport(option.value)}
              className="
                block w-full px-4 py-3
                text-left text-sm
                hover:bg-muted
              "
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title="Consumer MI"
          value={dashboardCounts.consumer}
          onClick={() => setActiveTab("Consumer MI")}
          loading={dashboardLoading}
        />

        <SummaryCard
          title="NSC"
          value={dashboardCounts.nsc}
          onClick={() => setActiveTab("NSC")}
          loading={dashboardLoading}
        />

        <SummaryCard
          title="DTR MI"
          value={dashboardCounts.dtr}
          onClick={() => setActiveTab("DTR MI")}
          loading={dashboardLoading}
        />

        <SummaryCard
          title="Sign Off"
          value={dashboardCounts.signoff}
          onClick={() => setActiveTab("Sign Off")}
          loading={dashboardLoading}
        />
      </div>

      <div className="rounded-xl border border-border bg-background p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">
              Installation & Operations
            </h2>

            <p className="mt-1 text-sm text-muted-foreground">
              Meter installation, inspection, payment and sign-off workflow.
            </p>
          </div>

          {renderImportButton()}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <WorkflowStep number="1" label="Meter Installation" />
          <WorkflowStep number="2" label="Site Inspection" />
          <WorkflowStep number="3" label="Payment" />
          <WorkflowStep number="4" label="Sign Off" />
        </div>
      </div>
    </div>
  );

  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("pps_admin_user") || "null"); } catch { return null; }
  }, []);
  const isAdmin = String(currentUser?.role || "").toUpperCase().replace(/\s+/g, "_") === "ADMIN" || String(currentUser?.role || "").toUpperCase() === "SUPER_ADMIN";

  const rawData = (row) => row?.data && typeof row.data === "object" ? row.data : row || {};
  const firstValue = (row, names) => { const d=rawData(row); const keys=Object.keys(d); for (const n of names) { const k=keys.find(x => String(x).trim().toLowerCase() === String(n).trim().toLowerCase()); if (k && d[k] !== "" && d[k] != null) return d[k]; } return ""; };
  const getMiDateValue = (row) => {
    const cat=getCategoryFromTab(activeTab);
    return firstValue(row, cat === "dtr" ? ["DTR MI Done Date","MI Done Date","MI DATE"] : cat === "nsc" ? ["NSC MI Done Date","MI Done Date","MI DATE"] : ["MI Done Date","MI DATE"]);
  };
  const parseMiDate = (value) => {
    const v=String(value||"").trim(); const m=v.match(/^(\\d{1,2})[-\\/](\\d{1,2})[-\\/](\\d{4})$/);
    if (m) return new Date(Number(m[3]),Number(m[2])-1,Number(m[1]));
    const iso=v.match(/^(\\d{4})[-\\/](\\d{1,2})[-\\/](\\d{1,2})/);
    if (iso) return new Date(Number(iso[1]),Number(iso[2])-1,Number(iso[3]));
    const d=new Date(v); return Number.isNaN(d.getTime()) ? null : d;
  };
  const dateKey = (d) => d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}` : "";
  const fieldValue = (row, field) => {
    const d=rawData(row); const lower=Object.keys(d).find(k=>String(k).trim().toLowerCase()===field.trim().toLowerCase());
    if (lower) return d[lower];
    const map={
      "Account ID":["Account ID","Consumer Ac ID","Account Number","Consumer Number"],
      "New Meter Serial No.":["New Meter Serial No.","New Meter Serial No","New Meter Serial Number","New Meter serial number","New Meter No","Meter No"],
      "Old Meter Serial No.":["Old Meter Serial No.","Old Meter No","Old Meter Number","Old Meter Serial Number"],
      "DTR Code":["Polaris DTR Code","DTR Code"], "Consumer Name":["Consumer Name","Name"],
      "Division":["Division"], "Sub Division":["Subdivision","Sub-Division","Sub Division"],
      "Substation":["Substation","Substation Name","Substation Name from field"],
      "Feeder":["Feeder","Feeder Name","Feeder Name from field"],
      "Technician Name":["MI DONE BY","Technician Name","technician_name","Technician"],
      "Technician ID":["Technician ID","MI Done By ID","Orion ID"]
    };
    return firstValue(row,map[field]||[field]);
  };
  const getHeaders = (rows) => { const set=[]; rows.forEach(r => Object.keys(rawData(r)).forEach(k=>{ if(k && !["sl no","serial no","serial number"].includes(String(k).trim().toLowerCase()) && !set.includes(k)) set.push(k); })); return set; };
  const yellowDefaults = ["Account ID","Consumer Ac ID","Consumer Name","New Meter Serial No.","New Meter Serial Number","Old Meter No","Old meter kW (MD)","Old meter Current kWh","MI Done Date","DTR MI Done Date","NSC MI Done Date","Division","Subdivision","Sub-Division","Substation","Feeder","MI DONE BY","Technician Name"];
  const searchFields=["Account ID","New Meter Serial No.","Old Meter Serial No.","DTR Code","Consumer Name","Feeder","Substation","Technician Name","Technician ID"];
  const categoryRows = useMemo(() => {
    const category=getCategoryFromTab(activeTab);
    if(!category) return [];
    return installations.filter(r=>!r?.category || String(r.category).toLowerCase()===category).sort((a,b)=>(parseMiDate(getMiDateValue(b))?.getTime()||0)-(parseMiDate(getMiDateValue(a))?.getTime()||0));
  },[activeTab,installations]);
  const headerOptions=useMemo(()=>getHeaders(categoryRows),[categoryRows]);
  const serialMap=useMemo(()=>{ const counters={}; const map={}; categoryRows.forEach(r=>{const d=parseMiDate(getMiDateValue(r)); const key=dateKey(d); if(!key){map[getInstallationId(r)]=String(++counters.blank||1); return;} counters[key]=(counters[key]||0)+1; const yy=String(d.getFullYear()).slice(-2), mm=String(d.getMonth()+1).padStart(2,"0"), dd=String(d.getDate()).padStart(2,"0"); map[getInstallationId(r)]=`${yy}${mm}${dd}${String(counters[key]).padStart(2,"0")}`;}); return map;},[categoryRows]);
  const visibleRows=useMemo(()=>categoryRows.filter(row=>{
    const text=String(fieldValue(row,searchField)||"").toLowerCase();
    if(searchText.trim() && !text.includes(searchText.trim().toLowerCase())) return false;
    const d=parseMiDate(getMiDateValue(row));
    if(filters.fromDate && (!d || dateKey(d)<filters.fromDate)) return false;
    if(filters.toDate && (!d || dateKey(d)>filters.toDate)) return false;
    for(const [key,field] of [["division","Division"],["subdivision","Sub Division"],["substation","Substation"],["feeder","Feeder"]]) if(filters[key] && String(fieldValue(row,field)||"")!==filters[key]) return false;
    if(filters.technician && !String(fieldValue(row,"Technician Name")||"").includes(filters.technician)) return false;
    return true;
  }),[categoryRows,searchField,searchText,filters]);
  const uniqueValues=(field, dependencyField="", dependencyValue="")=>Array.from(new Set(categoryRows.filter(r=>!dependencyField || !dependencyValue || String(fieldValue(r,dependencyField)||"")===dependencyValue).map(r=>String(fieldValue(r,field)||"").trim()).filter(Boolean))).sort();
  const getSelectedColumns=()=>selectedColumns[activeTab] || headerOptions.filter(h=>yellowDefaults.some(y=>String(h).trim().toLowerCase()===y.trim().toLowerCase() || String(h).toLowerCase().includes(y.toLowerCase())));
  const toggleColumn=(c)=>setSelectedColumns(prev=>({...prev,[activeTab]:getSelectedColumns().includes(c)?getSelectedColumns().filter(x=>x!==c):[...getSelectedColumns(),c]}));
  const setAllColumns=(all)=>setSelectedColumns(prev=>({...prev,[activeTab]:all?[...headerOptions]:[]}));
  const generateExport=async(mode,format)=>{
    setExporting(true); setExportMenu(false);
    try {
      const ids=mode==="polaris"?[]:visibleRows.map(getInstallationId); const payload={category:getCategoryFromTab(activeTab),mode,columns:getSelectedColumns(),installation_ids:ids,serial_numbers:serialMap};
      const response=await adminHttp.post(`/api/operations/mi/export/${format}`,payload,{responseType:"blob"});
      const blob=new Blob([response.data],{type:format==="excel"?"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":"application/pdf"});
      const url=URL.createObjectURL(blob), a=document.createElement("a"); a.href=url; a.download=`${getCategoryFromTab(activeTab)}-${mode}-export.${format==="excel"?"xlsx":"pdf"}`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch(err){ alert(apiError(err,"Unable to export data.")); } finally { setExporting(false); }
  };
  const deleteRow=async(row)=>{ if(!isAdmin){alert("Only Admin can delete imported data.");return;} if(!window.confirm("Delete this imported record? This action cannot be undone.")) return; try{await adminHttp.delete(`/api/operations/installations/${getInstallationId(row)}`); await refreshAll(); setSelectedRows(x=>x.filter(id=>id!==getInstallationId(row)));}catch(err){alert(apiError(err,"Unable to delete record."));} };
  const deleteSelected=async()=>{ if(!isAdmin||!selectedRows.length)return; if(!window.confirm(`Delete ${selectedRows.length} selected imported records? This action cannot be undone.`))return; try{await adminHttp.post("/api/operations/installations/delete-many",selectedRows); await refreshAll(); setSelectedRows([]);}catch(err){alert(apiError(err,"Unable to delete selected records."));} };

  const renderInstallationTab = () => {
    const isDtr=activeTab==="DTR MI";
    const cols=getSelectedColumns();
    const checkedAll=visibleRows.length>0 && visibleRows.every(r=>selectedRows.includes(getInstallationId(r)));
    const toggleAll=()=>setSelectedRows(checkedAll?[]:visibleRows.map(getInstallationId));
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-border bg-background p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <button type="button" onClick={()=>setImportMenu(v=>!v)} disabled={importing} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-semibold hover:bg-muted">{importing?<RefreshCw className="h-4 w-4 animate-spin"/>:<Upload className="h-4 w-4"/>}Import Data<ChevronDown className="h-3.5 w-3.5"/></button>
              {importMenu&&<MenuPanel className="left-0">{IMPORT_OPTIONS.map(o=><button key={o.value} type="button" onClick={()=>handleImport(o.value)} className="block w-full px-4 py-2.5 text-left text-sm hover:bg-muted">{o.label}</button>)}</MenuPanel>}
            </div>
            <div className="relative">
              <button type="button" onClick={()=>setExportMenu(v=>!v)} disabled={exporting} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-semibold hover:bg-muted">{exporting?<RefreshCw className="h-4 w-4 animate-spin"/>:<Download className="h-4 w-4"/>}Export<ChevronDown className="h-3.5 w-3.5"/></button>
              {exportMenu&&<MenuPanel className="left-0 w-60"><div className="px-4 py-2 text-xs font-bold text-muted-foreground">PPS EXPORT</div><button type="button" onClick={()=>generateExport("pps","excel")} className="block w-full px-4 py-2 text-left text-sm hover:bg-muted">Excel — Selected Columns</button><button type="button" onClick={()=>generateExport("pps","pdf")} className="block w-full px-4 py-2 text-left text-sm hover:bg-muted">PDF — Selected Columns</button><div className="my-1 border-t border-border"/><div className="px-4 py-2 text-xs font-bold text-muted-foreground">POLARIS EXPORT</div><button type="button" onClick={()=>generateExport("polaris","excel")} className="block w-full px-4 py-2 text-left text-sm hover:bg-muted">Excel — All Imported Data</button><button type="button" onClick={()=>generateExport("polaris","pdf")} className="block w-full px-4 py-2 text-left text-sm hover:bg-muted">PDF — All Imported Data</button></MenuPanel>}
            </div>
            <div className="relative ml-auto">
              <button type="button" onClick={()=>setSearchByMenu(v=>!v)} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-semibold hover:bg-muted"><Filter className="h-4 w-4"/>Search By<ChevronDown className="h-3.5 w-3.5"/></button>
              {searchByMenu&&<div className="absolute right-0 top-full z-50 mt-2 w-[330px] rounded-xl border border-border bg-background p-3 shadow-xl">
                <div className="mb-2 flex items-center justify-between"><span className="text-sm font-semibold">Search By</span><button type="button" onClick={()=>setSearchByMenu(false)}><X className="h-4 w-4"/></button></div>
                <div className="grid gap-2">
                  <label className="text-xs font-medium">MI Date</label><div className="grid grid-cols-2 gap-2"><input type="date" value={filters.fromDate} onChange={e=>setFilters(f=>({...f,fromDate:e.target.value}))} className="h-9 rounded-md border border-border bg-background px-2 text-xs"/><input type="date" value={filters.toDate} onChange={e=>setFilters(f=>({...f,toDate:e.target.value}))} className="h-9 rounded-md border border-border bg-background px-2 text-xs"/></div>
                  {[['division','Division','',''],['subdivision','Sub Division','Division',filters.division],['substation','Substation','Sub Division',filters.subdivision],['feeder','Feeder','Substation',filters.substation]].map(([key,label,dep,depVal])=><select key={key} value={filters[key]} onChange={e=>setFilters(f=>({...f,[key]:e.target.value,...(key==='division'?{subdivision:'',substation:'',feeder:''}:key==='subdivision'?{substation:'',feeder:''}:key==='substation'?{feeder:''}:{})}))} className="h-9 rounded-md border border-border bg-background px-2 text-xs"><option value="">All {label}</option>{uniqueValues(label,dep,depVal).map(v=><option key={v} value={v}>{v}</option>)}</select>)}
                  <select value={filters.technician} onChange={e=>setFilters(f=>({...f,technician:e.target.value}))} className="h-9 rounded-md border border-border bg-background px-2 text-xs"><option value="">All Technicians</option>{uniqueValues("Technician Name").map(v=><option key={v} value={v}>{v}</option>)}</select>
                  <div className="flex justify-end gap-2 pt-1"><button type="button" onClick={()=>setFilters({fromDate:"",toDate:"",division:"",subdivision:"",substation:"",feeder:"",technician:""})} className="rounded-md border border-border px-3 py-1.5 text-xs">Clear</button><button type="button" onClick={()=>setSearchByMenu(false)} className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">Apply</button></div>
                </div>
              </div>}
            </div>
            <div className="relative"><select value={searchField} onChange={e=>setSearchField(e.target.value)} className="h-9 rounded-l-lg border border-border bg-background px-2 text-xs font-semibold outline-none">{searchFields.map(f=><option key={f}>{f}</option>)}</select></div>
            <div className="relative -ml-2"><Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"/><input value={searchText} onChange={e=>setSearchText(e.target.value)} placeholder="Search..." className="h-9 w-52 rounded-r-lg border border-l-0 border-border bg-background pl-8 pr-3 text-sm outline-none focus:ring-1 focus:ring-primary"/></div>
            <div className="relative">
              <button type="button" onClick={()=>setColumnMenu(v=>!v)} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-semibold hover:bg-muted"><Columns3 className="h-4 w-4"/>Columns<ChevronDown className="h-3.5 w-3.5"/></button>
              {columnMenu&&<div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-xl border border-border bg-background p-3 shadow-xl"><div className="mb-2 flex items-center justify-between"><span className="text-sm font-semibold">Columns</span><button type="button" onClick={()=>setColumnMenu(false)}><X className="h-4 w-4"/></button></div><div className="mb-2 flex gap-2"><button type="button" onClick={()=>setAllColumns(true)} className="rounded border px-2 py-1 text-xs">Select All</button><button type="button" onClick={()=>setAllColumns(false)} className="rounded border px-2 py-1 text-xs">Clear</button><button type="button" onClick={()=>setSelectedColumns(p=>({...p,[activeTab]:headerOptions.filter(h=>yellowDefaults.some(y=>String(h).trim().toLowerCase()===y.trim().toLowerCase()||String(h).toLowerCase().includes(y.toLowerCase())))}))} className="rounded border px-2 py-1 text-xs">Yellow Default</button></div><div className="max-h-72 overflow-y-auto space-y-1">{headerOptions.map(c=><label key={c} className="flex items-center gap-2 rounded px-2 py-1 text-xs hover:bg-muted"><input type="checkbox" checked={cols.includes(c)} onChange={()=>toggleColumn(c)}/><span>{c}</span></label>)}</div></div>}
            </div>
            {isAdmin&&selectedRows.length>0&&<button type="button" onClick={deleteSelected} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-red-600 px-3 text-sm font-semibold text-white"><Trash2 className="h-4 w-4"/>Delete ({selectedRows.length})</button>}
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground"><span>{visibleRows.length} record(s)</span><span>{isDtr?"Site Inspection mandatory":"Site Inspection optional"}</span></div>
        </div>
        {error&&<ErrorBox message={error}/>}
        <div className="overflow-hidden rounded-xl border border-border bg-background"><div className="max-h-[calc(100vh-310px)] overflow-auto"><table className="min-w-max w-full text-sm"><thead className="sticky top-0 z-10 bg-muted"><tr><th className="px-3 py-2 text-left"><input type="checkbox" checked={checkedAll} onChange={toggleAll}/></th><th className="px-3 py-2 text-left font-semibold">Serial No.</th>{cols.map(c=><th key={c} className="whitespace-nowrap px-3 py-2 text-left font-semibold">{c}</th>)}<th className="sticky right-0 bg-muted px-3 py-2 text-left font-semibold">Action</th></tr></thead><tbody>{loading?<tr><td colSpan={cols.length+3} className="px-4 py-10 text-center text-muted-foreground">Loading installation data...</td></tr>:visibleRows.length===0?<tr><td colSpan={cols.length+3} className="px-4 py-10 text-center text-muted-foreground">No data found.</td></tr>:visibleRows.map((row,index)=>{const id=getInstallationId(row); return <tr key={id||index} className="border-t border-border hover:bg-muted/30"><td className="px-3 py-2"><input type="checkbox" checked={selectedRows.includes(id)} onChange={()=>setSelectedRows(x=>x.includes(id)?x.filter(v=>v!==id):[...x,id])}/></td><td className="px-3 py-2 font-semibold">{serialMap[id]||"-"}</td>{cols.map(c=><td key={c} className="max-w-[280px] whitespace-nowrap px-3 py-2" title={formatValue(fieldValue(row,c))}>{formatValue(fieldValue(row,c))}</td>)}<td className="sticky right-0 whitespace-nowrap bg-background px-3 py-2"><div className="flex gap-1.5"><button type="button" onClick={()=>setSelectedInstallation(row)} className="rounded-md border px-2 py-1 text-xs">Workflow</button><button type="button" onClick={()=>openInspection(row)} className="rounded-md border px-2 py-1 text-xs">Inspection</button>{isAdmin&&<button type="button" onClick={()=>deleteRow(row)} className="rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5"/></button>}</div></td></tr>})}</tbody></table></div></div>
      </div>
    );
  };

  const renderSignOff = () => (
    <div className="space-y-5">
      <div className="rounded-xl border border-border bg-background p-5">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <h2 className="text-lg font-semibold">Sign Off</h2>

            <p className="mt-1 text-sm text-muted-foreground">
              Generate sign-off batches using the configured templates.
            </p>
          </div>

          {renderImportButton()}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <label className="mb-2 block text-sm font-medium">
              Sign Off Type
            </label>

            <select
              value={selectedSignoffType}
              onChange={(event) =>
                setSelectedSignoffType(event.target.value)
              }
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary"
            >
              {SIGNOFF_TYPES.map((type) => (
                <option
                  key={type.value}
                  value={type.value}
                  disabled={type.disabled}
                >
                  {type.label}
                  {type.disabled ? " — Not available in supplied template" : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end gap-2">
            <button
              type="button"
              disabled={signoffLoading}
              onClick={() => generateSignoff("excel")}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-semibold hover:bg-muted disabled:opacity-50"
            >
              <FileDown className="h-4 w-4" />
              Excel
            </button>

            <button
              type="button"
              disabled={signoffLoading}
              onClick={generatePdf}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              <FileDown className="h-4 w-4" />
              PDF
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-background">
        <div className="border-b border-border px-5 py-4">
          <h3 className="font-semibold">Available Sign Off Data</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[800px] w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">#</th>
                <th className="px-4 py-3 text-left font-semibold">
                  Consumer / Site
                </th>
                <th className="px-4 py-3 text-left font-semibold">
                  Meter / Material
                </th>
                <th className="px-4 py-3 text-left font-semibold">
                  Date
                </th>
                <th className="px-4 py-3 text-left font-semibold">
                  Status
                </th>
              </tr>
            </thead>

            <tbody>
              {signoffData.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-10 text-center text-muted-foreground"
                  >
                    No sign-off data available.
                  </td>
                </tr>
              ) : (
                signoffData.map((row, index) => (
                  <tr
                    key={row?.id || row?._id || index}
                    className="border-t border-border"
                  >
                    <td className="px-4 py-3">{index + 1}</td>

                    <td className="px-4 py-3">
                      {getConsumerName(row)}
                    </td>

                    <td className="px-4 py-3">
                      {getMeterNumber(row)}
                    </td>

                    <td className="px-4 py-3">
                      {formatDate(
                        row?.date ||
                          row?.signoff_date ||
                          row?.created_at
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <StatusBadge value={getStatus(row)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-background">
        <div className="border-b border-border px-5 py-4">
          <h3 className="font-semibold">Generated Sign Off Batches</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[700px] w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Batch</th>
                <th className="px-4 py-3 text-left font-semibold">Type</th>
                <th className="px-4 py-3 text-left font-semibold">Date</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
              </tr>
            </thead>

            <tbody>
              {signoffBatches.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-10 text-center text-muted-foreground"
                  >
                    No sign-off batches generated yet.
                  </td>
                </tr>
              ) : (
                signoffBatches.map((batch, index) => (
                  <tr
                    key={batch?.id || batch?._id || index}
                    className="border-t border-border"
                  >
                    <td className="px-4 py-3">
                      {batch?.batch_number ||
                        batch?.batch_id ||
                        batch?.id ||
                        "-"}
                    </td>

                    <td className="px-4 py-3">
                      {batch?.signoff_type ||
                        batch?.type ||
                        selectedSignoffType}
                    </td>

                    <td className="px-4 py-3">
                      {formatDate(
                        batch?.created_at ||
                          batch?.date ||
                          batch?.signoff_date
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <StatusBadge
                        value={batch?.status || "Generated"}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout
      title="Installation & Operations"
      subtitle="Manage meter installation, inspection, payment and sign off operations."
    >
      <div className="w-full">
        <div className="mb-5 flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h1 className="text-2xl font-bold">
              Installation & Operations
            </h1>

            <p className="mt-1 text-sm text-muted-foreground">
              Manage meter installation, inspection, payment and sign off
              operations.
            </p>
          </div>

          <button
            type="button"
            onClick={refreshAll}
            disabled={loading || dashboardLoading}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${
                loading || dashboardLoading ? "animate-spin" : ""
              }`}
            />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-5">
            <ErrorBox message={error} />
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-border bg-background">
          <div className="flex min-w-max">
            {TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`
                  border-b-2 px-5 py-3 text-sm font-semibold transition-colors
                  ${
                    activeTab === tab
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                  }
                `}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          {activeTab === "Overview" && renderOverview()}

          {["Consumer MI", "NSC", "DTR MI"].includes(activeTab) &&
            renderInstallationTab()}

          {activeTab === "Sign Off" && renderSignOff()}
        </div>

        {selectedInstallation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-xl bg-background shadow-2xl">
              <div className="border-b border-border px-5 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">
                      Installation Workflow
                    </h3>

                    <p className="mt-1 text-xs text-muted-foreground">
                      {getConsumerName(selectedInstallation)}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setSelectedInstallation(null)}
                    className="rounded-md px-2 py-1 text-sm hover:bg-muted"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="space-y-4 p-5">
                <WorkflowRow
                  number="1"
                  title="Meter Installation"
                  status={getStatus(selectedInstallation)}
                />

                <div className="rounded-lg border border-border p-4">
                  <div className="mb-3">
                    <div className="font-semibold">
                      2. Site Inspection
                    </div>

                    <div className="mt-1 text-xs text-muted-foreground">
                      Choose whether inspection is required.
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={inspectionLoading}
                      onClick={() =>
                        saveInspectionStatus(
                          selectedInstallation,
                          true
                        )
                      }
                      className="rounded-lg border border-border px-4 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
                    >
                      Required
                    </button>

                    <button
                      type="button"
                      disabled={inspectionLoading}
                      onClick={() =>
                        saveInspectionStatus(
                          selectedInstallation,
                          false
                        )
                      }
                      className="rounded-lg border border-border px-4 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
                    >
                      Not Required
                    </button>

                    <button
                      type="button"
                      onClick={() => openInspection(selectedInstallation)}
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open Inspection
                    </button>
                  </div>
                </div>

                <WorkflowRow
                  number="3"
                  title="Payment"
                  status={
                    selectedInstallation?.payment_status ||
                    selectedInstallation?.payment?.status ||
                    "Pending"
                  }
                />

                <button
                  type="button"
                  disabled={paymentLoading}
                  onClick={() => markPaymentReady(selectedInstallation)}
                  className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  Mark Payment Ready
                </button>

                <WorkflowRow
                  number="4"
                  title="Sign Off"
                  status="Pending"
                />

                <button
                  type="button"
                  onClick={() => {
                    setSelectedInstallation(null);
                    setActiveTab("Sign Off");
                  }}
                  className="w-full rounded-lg border border-border px-4 py-2.5 text-sm font-semibold hover:bg-muted"
                >
                  Go to Sign Off
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function MenuPanel({ children, className = "" }) {
  return <div className={`absolute top-full z-50 mt-2 w-64 overflow-hidden rounded-xl border border-border bg-background shadow-xl ${className}`}>{children}</div>;
}

function SummaryCard({ title, value, onClick, loading }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl border border-border bg-background p-5 text-left transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="text-sm text-muted-foreground">{title}</div>

      <div className="mt-2 text-3xl font-bold">
        {loading ? "..." : value}
      </div>

      <div className="mt-3 text-xs font-medium text-primary">
        Open →
      </div>
    </button>
  );
}

function WorkflowStep({ number, label }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border p-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
        {number}
      </div>

      <div className="text-sm font-medium">{label}</div>
    </div>
  );
}

function WorkflowRow({ number, title, status }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-bold">
          {number}
        </div>

        <span className="text-sm font-semibold">{title}</span>
      </div>

      <StatusBadge value={status} />
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {formatValue(message)}
    </div>
  );
}

export default InstallationOperationsPage;